import React, { useEffect, useState } from "react";
import api from "../../api";
import { Loader2, Megaphone, Send, BarChart3 } from "lucide-react";
import { toast } from "../../hooks/use-toast";

const fmtDate = (d) => { try { return new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }); } catch { return ""; } };

export default function MarketingTab() {
  const [events, setEvents] = useState([]);
  const [discounts, setDiscounts] = useState([]);
  const [audience, setAudience] = useState(null);
  const [promos, setPromos] = useState([]);
  const [form, setForm] = useState({ target_event_id: "", subject: "", message: "", discount_code: "", include_details: true, segment: "all", city: "" });
  const [busy, setBusy] = useState(false);
  const cities = [...new Set(events.map((e) => e.city).filter(Boolean))].sort();

  const loadResults = () => api.get("/business/promos").then(({ data }) => setPromos(data.promos || [])).catch(() => {});
  const loadAudience = (segment, city) => api.get(`/business/marketing/audience?segment=${segment}&city=${encodeURIComponent(city || "")}`).then(({ data }) => setAudience(data.count)).catch(() => setAudience(0));
  useEffect(() => {
    api.get("/business/products").then(({ data }) => setEvents((data || []).filter((e) => !e.cancelled && !e.archived))).catch(() => {});
    api.get("/business/discounts").then(({ data }) => setDiscounts(data || [])).catch(() => {});
    loadAudience("all", "");
    loadResults();
  }, []);
  useEffect(() => { loadAudience(form.segment, form.city); }, [form.segment, form.city]); // eslint-disable-line

  const send = async (preview = false) => {
    if (!form.target_event_id) { toast({ title: "Pick a show to promote", variant: "destructive" }); return; }
    if (!form.message.trim()) { toast({ title: "Write a short message", variant: "destructive" }); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/business/marketing/broadcast", { ...form, preview });
      if (preview) toast({ title: "Test sent to you ✉️", description: `Check ${data.to}.` });
      else { toast({ title: "Broadcast sent 🎉", description: `Emailed ${data.sent} ticket-holder${data.sent !== 1 ? "s" : ""}${data.failed ? ` (${data.failed} failed)` : ""}.` }); setForm({ ...form, target_event_id: "", subject: "", message: "", discount_code: "" }); loadResults(); }
    } catch (e) { toast({ title: "Couldn't send", description: e?.response?.data?.detail || "Try again.", variant: "destructive" }); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h2 className="text-xl font-bold text-[#16243b] flex items-center gap-2"><Megaphone size={22} className="text-[#2ec9c9]" /> Marketing</h2>
        <p className="text-[#5a6b80] text-sm mt-1">Email <strong>all {audience != null ? audience : "…"} of your current ticket-holders</strong> to promote any show. Opens, clicks and tickets driven are tracked below.</p>
      </div>

      <div className="bg-white rounded-2xl p-6 shadow-[0_8px_24px_rgba(20,30,55,0.06)] space-y-3" data-testid="marketing-compose">
        <div>
          <label className="text-xs font-medium text-[#8a97a8] mb-1.5 block">Show to promote</label>
          <select value={form.target_event_id} onChange={(e) => setForm({ ...form, target_event_id: e.target.value })} data-testid="marketing-target"
            className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9] text-sm">
            <option value="">Choose a show…</option>
            {events.map((e) => <option key={e.id} value={e.id}>{e.title}{e.event_date ? ` — ${fmtDate(e.event_date)}` : ""}</option>)}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-medium text-[#8a97a8] mb-1.5 block">Audience</label>
            <select value={form.segment} onChange={(e) => setForm({ ...form, segment: e.target.value })} data-testid="marketing-segment"
              className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9] text-sm">
              <option value="all">All ticket-holders</option>
              <option value="past">Past-event attendees</option>
              <option value="upcoming">Upcoming ticket-holders</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-[#8a97a8] mb-1.5 block">City <span className="text-[#b6c0cd]">(optional)</span></label>
            <select value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="marketing-city"
              className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9] text-sm">
              <option value="">All cities</option>
              {cities.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>
        <div>
          <label className="text-xs font-medium text-[#8a97a8] mb-1.5 block">Subject <span className="text-[#b6c0cd]">(optional)</span></label>
          <input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="marketing-subject" placeholder="e.g. New show just announced 🎉"
            className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9] text-sm" />
        </div>
        <div>
          <label className="text-xs font-medium text-[#8a97a8] mb-1.5 block">Your message</label>
          <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="marketing-message" rows={4}
            placeholder="Thanks for coming to our shows! We've got something special coming up…"
            className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9] text-sm resize-none" />
        </div>
        <div>
          <label className="text-xs font-medium text-[#8a97a8] mb-1.5 block">Feature a promo code <span className="text-[#b6c0cd]">(optional)</span></label>
          <select value={form.discount_code} onChange={(e) => setForm({ ...form, discount_code: e.target.value })} data-testid="marketing-discount"
            className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9] text-sm">
            <option value="">No code</option>
            {discounts.map((d) => <option key={d.id} value={d.code}>{d.code}</option>)}
          </select>
        </div>
        <label className="flex items-center gap-2 text-sm text-[#5a6b80] cursor-pointer">
          <input type="checkbox" checked={form.include_details} onChange={(e) => setForm({ ...form, include_details: e.target.checked })} data-testid="marketing-include-details" className="w-4 h-4 accent-[#2ec9c9]" />
          Include the show's date, venue &amp; price block
        </label>
        <div className="flex items-center justify-between gap-2 pt-1">
          <button onClick={() => send(true)} disabled={busy || !form.target_event_id || !form.message.trim()} data-testid="marketing-send-test"
            className="text-sm text-[#1fb0b0] font-medium hover:underline disabled:opacity-40 disabled:no-underline">Send test to me</button>
          <button onClick={() => send(false)} disabled={busy || !form.target_event_id || !form.message.trim()} data-testid="marketing-send"
            className="gp-btn-teal rounded-xl px-6 py-2.5 text-sm font-medium disabled:opacity-60 flex items-center gap-2">
            {busy ? <Loader2 className="animate-spin" size={15} /> : <Send size={15} />} Send to all {audience != null ? audience : ""} holders
          </button>
        </div>
      </div>

      <div>
        <h3 className="font-semibold text-[#16243b] flex items-center gap-2 mb-3"><BarChart3 size={18} className="text-[#2ec9c9]" /> Campaign results</h3>
        {promos.length === 0 ? (
          <p className="text-sm text-[#8a97a8] bg-white rounded-2xl p-6 text-center" data-testid="marketing-no-results">No campaigns yet. Send your first broadcast above.</p>
        ) : (
          <div className="space-y-3">
            {promos.map((c) => (
              <div key={c.id} className="bg-white rounded-2xl border border-gray-100 p-4" data-testid={`marketing-result-${c.id}`}>
                <p className="font-semibold text-[#16243b]">{c.target_title}</p>
                <p className="text-xs text-[#8a97a8] mb-2">{c.subject}{c.created_at ? ` · ${fmtDate(c.created_at)}` : ""}</p>
                <div className="grid grid-cols-5 gap-2 text-center">
                  {[["Sent", c.sent], ["Opens", c.opens], ["Clicks", c.clicks], ["Tickets", c.tickets_driven], ["Revenue", `£${(c.revenue_driven || 0).toFixed(2)}`]].map(([k, v]) => (
                    <div key={k}><p className="text-base font-bold text-[#16243b]">{v == null ? "—" : v}</p><p className="text-[10px] text-[#8a97a8] uppercase">{k}</p></div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
